import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";

export type GeocodeStatus = "idle" | "loading" | "resolved" | "notFound";

export interface GeocodedAddress {
  coordinates: { lat: number; lng: number };
  formattedAddress: string;
}

export interface UseGeocodedAddressReturn {
  status: GeocodeStatus;
  result: GeocodedAddress | null;
}

/**
 * Resolves an address handed to the app in the URL by an external system into
 * the coordinates the nearby search needs.
 *
 * Runs at most once per address: a hit from here only seeds the initial view,
 * and re-running would fight whatever the user searched for afterwards.
 */
export const useGeocodedAddress = (
  address: string | null,
): UseGeocodedAddressReturn => {
  const geocodingLibrary = useMapsLibrary("geocoding");
  const [status, setStatus] = useState<GeocodeStatus>("idle");
  const [result, setResult] = useState<GeocodedAddress | null>(null);
  const geocodedAddress = useRef<string | null>(null);

  useEffect(() => {
    if (!address || !geocodingLibrary) return;
    if (geocodedAddress.current === address) return;
    geocodedAddress.current = address;

    let cancelled = false;
    setStatus("loading");

    const geocoder = new (geocodingLibrary.Geocoder as any)();
    geocoder
      .geocode({ address })
      .then(({ results }: google.maps.GeocoderResponse) => {
        if (cancelled) return;
        const match = results?.[0];
        if (!match) {
          setStatus("notFound");
          return;
        }
        setResult({
          coordinates: match.geometry.location.toJSON(),
          formattedAddress: match.formatted_address,
        });
        setStatus("resolved");
      })
      .catch(() => {
        // The Geocoder rejects on ZERO_RESULTS as well as on transport
        // failures, and the user's remedy is the same either way: search again.
        if (!cancelled) setStatus("notFound");
      });

    return () => {
      cancelled = true;
    };
  }, [address, geocodingLibrary]);

  return { status, result };
};
